import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ideas, config, Archetype } from '../lib/api'

export default function NewIdeaPage() {
  const navigate = useNavigate()
  const [title, setTitle] = useState('')
  const [rawGoal, setRawGoal] = useState('')
  const [archetypeId, setArchetypeId] = useState<string | undefined>()
  const [archetypes, setArchetypes] = useState<Archetype[]>([])
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState('')

  // Load archetypes
  useEffect(() => {
    config
      .getArchetypes()
      .then((data) => setArchetypes(data))
      .catch(() => {
        // Archetypes endpoint may not exist yet
      })
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) return

    setCreating(true)
    setError('')

    try {
      const idea = await ideas.create({
        title: title.trim(),
        raw_goal: rawGoal.trim(),
        archetype_id: archetypeId,
      })
      navigate(`/ideas/${idea.idea_id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create idea')
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="flex-1 overflow-y-auto px-4 py-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-gray-200 mb-4 transition"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Ideas
          </button>
          <h1 className="text-2xl font-bold text-gray-100">New Idea</h1>
          <p className="text-sm text-gray-400 mt-1">
            Describe your idea and we'll help you develop it into something real.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div>
            <label
              htmlFor="title"
              className="block text-sm font-medium text-gray-300 mb-2"
            >
              Idea Title
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What's your idea called?"
              className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition"
              required
              autoFocus
            />
          </div>

          {/* Raw goal */}
          <div>
            <label
              htmlFor="goal"
              className="block text-sm font-medium text-gray-300 mb-2"
            >
              Describe Your Goal
            </label>
            <textarea
              id="goal"
              value={rawGoal}
              onChange={(e) => setRawGoal(e.target.value)}
              placeholder="What problem does this solve? Who is it for? What does success look like?"
              className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition resize-none"
              rows={5}
            />
            <p className="text-xs text-gray-600 mt-1">
              The more detail you provide, the better the AI can help develop your idea.
            </p>
          </div>

          {/* Archetype selector */}
          {archetypes.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Idea Type (Optional)
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {archetypes.map((arch) => (
                  <button
                    key={arch.id}
                    type="button"
                    onClick={() =>
                      setArchetypeId(archetypeId === arch.id ? undefined : arch.id)
                    }
                    className={`p-3 rounded-lg border text-left transition ${
                      archetypeId === arch.id
                        ? 'border-brand-500 bg-brand-900/20 ring-1 ring-brand-500/30'
                        : 'border-gray-800 bg-gray-900 hover:border-gray-700'
                    }`}
                  >
                    <p
                      className={`text-sm font-medium ${
                        archetypeId === arch.id ? 'text-brand-300' : 'text-gray-200'
                      }`}
                    >
                      {arch.label}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                      {arch.description}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-950 border border-red-800 rounded-lg px-4 py-3 text-sm text-red-400">
              {error}
            </div>
          )}

          {/* Submit */}
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={!title.trim() || creating}
              className="px-6 py-2.5 bg-brand-600 hover:bg-brand-700 text-white font-medium text-sm rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {creating ? 'Creating...' : 'Create Idea'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2.5 text-gray-400 hover:text-gray-200 text-sm transition"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
